/**
 * What is wrong, said in physics (engine target §11).
 *
 * Nothing here is a validation error. A 'stops' is an arrangement about which
 * there is nothing true to say; a 'warns' is usually a STATE worth naming —
 * an object at the focus, a virtual object — which is an answer, not a fault.
 */
import { defOf } from './registry';
import { ImageReport, Part, Problem, Ray, World } from './types';

const nameOf = (w: World, id: string): string => {
    const p = w.parts.find(x => x.id === id) as Part;
    return defOf(p).name(p, w);
};

export function diagnose(w: World, rays: Ray[], images: ImageReport[]): Problem[] {
    const out: Problem[] = [];

    const lit = w.parts.filter(p => defOf(p).points || defOf(p).emit);
    if (lit.length === 0 && w.parts.length > 0) {
        out.push({ level: 'stops', text: 'Nothing is emitting light: add an object, a lamp or a ray box.' });
    }

    for (const obj of w.parts.filter(p => defOf(p).points && p.mount === 'axis')) {
        for (const el of w.parts.filter(p => defOf(p).element && p.mount === 'axis')) {
            if (Math.abs(el.at.x - obj.at.x) < 1e-9) {
                out.push({
                    level: 'stops', partIds: [obj.id, el.id],
                    text: `${nameOf(w, obj.id)} and ${nameOf(w, el.id)} are at the same place, so there is no image to find.`,
                });
            }
        }
    }

    for (const img of images) {
        const obj = nameOf(w, img.partId);
        const infinite = img.points.filter(p => p.kind === 'at-infinity');
        if (img.points.length > 0 && infinite.length === img.points.length) {
            const first = img.stages[img.stages.length - 1];
            out.push({
                level: 'warns', partIds: [img.partId],
                text: first
                    ? `${obj} is at the focus of ${first.name}. The rays leave it parallel, so there is no image: the answer to "where" is at infinity.`
                    : `The rays from ${obj} leave parallel, so its image is at infinity.`,
            });
        }
        if (img.pieces.length >= 2) {
            out.push({
                level: 'warns', partIds: [img.partId],
                text: `${obj} crosses the focal plane, so its image is in two pieces running off in opposite directions — the part at the focus images at infinity.`,
            });
        }
        const unmet = img.points.find(p => p.kind === 'none' && p.why?.startsWith('These rays'));
        if (unmet) out.push({ level: 'warns', partIds: [img.partId], text: unmet.why as string });

        for (const s of img.stages.filter(s => s.virtualObject)) {
            out.push({
                level: 'warns', partIds: [s.partId],
                text: `The image before ${s.name} falls behind it, so ${s.name} sees it as a virtual object (u = +${s.u.toFixed(1)} cm).`,
            });
        }
    }

    // A mirror reflects on one side only; light that reaches its back stops there.
    // Silently stopped rays read as a broken bench, so say which mirror and why.
    const surfaces = w.parts.flatMap(p => defOf(p).surfaces(p, w));
    const backOf = new Set<string>();
    for (const ray of rays) {
        const last = ray.vertices[ray.vertices.length - 1];
        const s = last.surfaceId ? surfaces.find(x => x.id === last.surfaceId) : undefined;
        if (ray.ended === 'absorbed' && s?.rule.law === 'ideal' && s.rule.folds) backOf.add(s.partId);
    }
    for (const id of backOf) {
        out.push({
            level: 'warns', partIds: [id],
            text: `The light reaches the back of ${nameOf(w, id)}, which does not reflect. Turn it round to face the light.`,
        });
    }

    const tirAt = new Map<string, { ray: string; incidence: number; n1: number; n2: number }>();
    for (const ray of rays) {
        for (const v of ray.vertices) {
            if (v.tir && v.surfaceId && !tirAt.has(v.surfaceId)) {
                tirAt.set(v.surfaceId, { ray: ray.id, incidence: v.incidence as number, n1: v.n1 as number, n2: v.n2 as number });
            }
        }
        if (ray.ended === 'bounce-cap') {
            out.push({ level: 'warns', rayIds: [ray.id], text: 'This ray was still going after 64 reflections, and the trace was stopped there.' });
        }
    }
    for (const [surfaceId, t] of tirAt) {
        const s = surfaces.find(x => x.id === surfaceId);
        const critical = (Math.asin(t.n2 / t.n1) * 180) / Math.PI;
        out.push({
            level: 'warns', surfaceId, rayIds: [t.ray],
            text: `Total internal reflection at ${s?.name ?? 'a face'}: the ray arrives at ${t.incidence.toFixed(1)}° and the critical angle for n = ${t.n1.toFixed(2)} is ${critical.toFixed(1)}°.`,
        });
    }
    return out;
}
